import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken } from './api';

// Minimal portal-wide i18n. Keep the string map tight (nav labels + a few CTAs)
// so adding a language stays a 15-min copy job.
const STRINGS = {
  en: {
    'nav.dashboard': 'Dashboard', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Leads', 'nav.contacts': 'Contacts',
    'nav.loans': 'Loans', 'nav.calc': 'Calculators', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Settings', 'nav.refer': 'Refer a Broker', 'nav.more': 'More',
    'action.new': 'New', 'action.signout': 'Sign out', 'welcome': 'Welcome back', 'lang.picker': 'Language',
    'nav.calendar': 'Calendar', 'nav.deals': 'Deals', 'nav.realestate': 'Real Estate',
    'nav.reports': 'Reports', 'nav.tasks': 'Tasks', 'nav.inbox': 'Inbox',
    'nav.account': 'Account', 'nav.help': 'Help & Training', 'nav.compliancekit': 'Compliance Kit', 'nav.feeworksheet': 'Fee Worksheet',
    'nav.mcr': 'Mortgage Call Reports', 'nav.income': 'Income Calculator', 'nav.documents': 'Documents',
    'nav.commissions': 'Commissions', 'nav.automations': 'Automations', 'nav.marketingstudio': 'Marketing Studio', 'nav.emailanalytics': 'Email Analytics',
    'nav.group.tools': 'Tools', 'nav.group.marketingops': 'Marketing & Ops', 'nav.org': 'Organization Settings',
    'nav.brand': 'Brand & Website', 'nav.billing': 'Billing', 'nav.enterprise': 'Enterprise',
    'nav.workspace': 'Workspace', 'add.loan': 'New Loan File', 'add.lead': 'New Lead',
    'add.contact': 'New Contact', 'add.transaction': 'New Transaction', 'add.task': 'New Task',
  },
  es: {
    'nav.dashboard': 'Panel', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Leads', 'nav.contacts': 'Contactos',
    'nav.loans': 'Expedientes', 'nav.calc': 'Calculadoras', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Ajustes', 'nav.refer': 'Recomienda', 'nav.more': 'Más',
    'action.new': 'Nuevo', 'action.signout': 'Salir', 'welcome': 'Bienvenido', 'lang.picker': 'Idioma',
    'nav.calendar': 'Calendario', 'nav.deals': 'Negocios', 'nav.realestate': 'Bienes raíces',
    'nav.reports': 'Informes', 'nav.tasks': 'Tareas', 'nav.inbox': 'Bandeja',
    'nav.account': 'Cuenta', 'nav.help': 'Ayuda y formación', 'nav.compliancekit': 'Kit de cumplimiento', 'nav.feeworksheet': 'Hoja de tarifas',
    'nav.mcr': 'Informes de llamadas hipotecarias', 'nav.income': 'Calculadora de ingresos', 'nav.documents': 'Documentos',
    'nav.commissions': 'Comisiones', 'nav.automations': 'Automatizaciones', 'nav.marketingstudio': 'Estudio de Marketing', 'nav.emailanalytics': 'Analítica de Email',
    'nav.group.tools': 'Herramientas', 'nav.group.marketingops': 'Marketing y operaciones', 'nav.org': 'Ajustes de la organización',
    'nav.brand': 'Marca y sitio web', 'nav.billing': 'Facturación', 'nav.enterprise': 'Enterprise',
    'nav.workspace': 'Espacio de trabajo', 'add.loan': 'Nuevo expediente', 'add.lead': 'Nuevo lead',
    'add.contact': 'Nuevo contacto', 'add.transaction': 'Nueva transacción', 'add.task': 'Nueva tarea',
  },
  pt: {
    'nav.dashboard': 'Painel', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Leads', 'nav.contacts': 'Contatos',
    'nav.loans': 'Arquivos', 'nav.calc': 'Calculadoras', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Ajustes', 'nav.refer': 'Indique', 'nav.more': 'Mais',
    'action.new': 'Novo', 'action.signout': 'Sair', 'welcome': 'Bem-vindo', 'lang.picker': 'Idioma',
    'nav.calendar': 'Calendário', 'nav.deals': 'Negócios', 'nav.realestate': 'Imóveis',
    'nav.reports': 'Relatórios', 'nav.tasks': 'Tarefas', 'nav.inbox': 'Caixa de entrada',
    'nav.account': 'Conta', 'nav.help': 'Ajuda e treinamento', 'nav.compliancekit': 'Kit de conformidade', 'nav.feeworksheet': 'Planilha de taxas',
    'nav.mcr': 'Relatórios de chamadas hipotecárias', 'nav.income': 'Calculadora de renda', 'nav.documents': 'Documentos',
    'nav.commissions': 'Comissões', 'nav.automations': 'Automações', 'nav.marketingstudio': 'Estúdio de Marketing', 'nav.emailanalytics': 'Análise de E-mail',
    'nav.group.tools': 'Ferramentas', 'nav.group.marketingops': 'Marketing e operações', 'nav.org': 'Configurações da organização',
    'nav.brand': 'Marca e site', 'nav.billing': 'Cobrança', 'nav.enterprise': 'Enterprise',
    'nav.workspace': 'Espaço de trabalho', 'add.loan': 'Novo arquivo', 'add.lead': 'Novo lead',
    'add.contact': 'Novo contato', 'add.transaction': 'Nova transação', 'add.task': 'Nova tarefa',
  },
  zh: {
    'nav.dashboard': '仪表板', 'nav.pipeline': '管道', 'nav.leads': '潜在客户', 'nav.contacts': '联系人',
    'nav.loans': '贷款档案', 'nav.calc': '计算器', 'nav.ai': 'Audrey AI', 'nav.marketing': '营销',
    'nav.settings': '设置', 'nav.refer': '推荐经纪人', 'nav.more': '更多',
    'action.new': '新建', 'action.signout': '登出', 'welcome': '欢迎回来', 'lang.picker': '语言',
    'nav.calendar': '日历', 'nav.deals': '交易', 'nav.realestate': '房地产',
    'nav.reports': '报告', 'nav.tasks': '任务', 'nav.inbox': '收件箱',
    'nav.account': '账户', 'nav.help': '帮助与培训', 'nav.compliancekit': '合规工具包', 'nav.feeworksheet': '费用表',
    'nav.mcr': '抵押贷款通话报告', 'nav.income': '收入计算器', 'nav.documents': '文档',
    'nav.commissions': '佣金', 'nav.automations': '自动化', 'nav.marketingstudio': '营销工作室', 'nav.emailanalytics': '邮件分析',
    'nav.group.tools': '工具', 'nav.group.marketingops': '营销与运营', 'nav.org': '组织设置',
    'nav.brand': '品牌与网站', 'nav.billing': '账单', 'nav.enterprise': '企业版',
    'nav.workspace': '工作区', 'add.loan': '新建贷款档案', 'add.lead': '新建潜在客户',
    'add.contact': '新建联系人', 'add.transaction': '新建交易', 'add.task': '新建任务',
  },
  fr: {
    'nav.dashboard': 'Tableau de bord', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Prospects', 'nav.contacts': 'Contacts',
    'nav.loans': 'Dossiers', 'nav.calc': 'Calculatrices', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Paramètres', 'nav.refer': 'Parrainer', 'nav.more': 'Plus',
    'action.new': 'Nouveau', 'action.signout': 'Déconnexion', 'welcome': 'Bon retour', 'lang.picker': 'Langue',
    'nav.calendar': 'Calendrier', 'nav.deals': 'Dossiers clients', 'nav.realestate': 'Immobilier',
    'nav.reports': 'Rapports', 'nav.tasks': 'Tâches', 'nav.inbox': 'Boîte de réception',
    'nav.account': 'Compte', 'nav.help': 'Aide et formation', 'nav.compliancekit': 'Kit de conformité', 'nav.feeworksheet': 'Feuille de frais',
    'nav.mcr': 'Rapports hypothécaires', 'nav.income': 'Calculateur de revenus', 'nav.documents': 'Documents',
    'nav.commissions': 'Commissions', 'nav.automations': 'Automatisations', 'nav.marketingstudio': 'Studio Marketing', 'nav.emailanalytics': 'Analyses des e-mails',
    'nav.group.tools': 'Outils', 'nav.group.marketingops': 'Marketing et opérations', 'nav.org': "Paramètres de l'organisation",
    'nav.brand': 'Marque et site web', 'nav.billing': 'Facturation', 'nav.enterprise': 'Entreprise',
    'nav.workspace': 'Espace de travail', 'add.loan': 'Nouveau dossier', 'add.lead': 'Nouveau prospect',
    'add.contact': 'Nouveau contact', 'add.transaction': 'Nouvelle transaction', 'add.task': 'Nouvelle tâche',
  },
  de: {
    'nav.dashboard': 'Übersicht', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Leads', 'nav.contacts': 'Kontakte',
    'nav.loans': 'Kreditakten', 'nav.calc': 'Rechner', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Einstellungen', 'nav.refer': 'Empfehlen', 'nav.more': 'Mehr',
    'action.new': 'Neu', 'action.signout': 'Abmelden', 'welcome': 'Willkommen zurück', 'lang.picker': 'Sprache',
    'nav.calendar': 'Kalender', 'nav.deals': 'Vorgänge', 'nav.realestate': 'Immobilien',
    'nav.reports': 'Berichte', 'nav.tasks': 'Aufgaben', 'nav.inbox': 'Posteingang',
    'nav.account': 'Konto', 'nav.help': 'Hilfe & Schulung', 'nav.compliancekit': 'Compliance-Kit', 'nav.feeworksheet': 'Gebührenblatt',
    'nav.mcr': 'Hypotheken-Meldeberichte', 'nav.income': 'Einkommensrechner', 'nav.documents': 'Dokumente',
    'nav.commissions': 'Provisionen', 'nav.automations': 'Automatisierungen', 'nav.marketingstudio': 'Marketing-Studio', 'nav.emailanalytics': 'E-Mail-Analyse',
    'nav.group.tools': 'Werkzeuge', 'nav.group.marketingops': 'Marketing & Betrieb', 'nav.org': 'Organisationseinstellungen',
    'nav.brand': 'Marke & Website', 'nav.billing': 'Abrechnung', 'nav.enterprise': 'Enterprise',
    'nav.workspace': 'Arbeitsbereich', 'add.loan': 'Neue Kreditakte', 'add.lead': 'Neuer Lead',
    'add.contact': 'Neuer Kontakt', 'add.transaction': 'Neue Transaktion', 'add.task': 'Neue Aufgabe',
  },
  it: {
    'nav.dashboard': 'Cruscotto', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Contatti', 'nav.contacts': 'Rubrica',
    'nav.loans': 'Pratiche', 'nav.calc': 'Calcolatori', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Marketing',
    'nav.settings': 'Impostazioni', 'nav.refer': 'Invita', 'nav.more': 'Altro',
    'action.new': 'Nuovo', 'action.signout': 'Esci', 'welcome': 'Bentornato', 'lang.picker': 'Lingua',
    'nav.calendar': 'Calendario', 'nav.deals': 'Trattative', 'nav.realestate': 'Immobiliare',
    'nav.reports': 'Report', 'nav.tasks': 'Attività', 'nav.inbox': 'Posta in arrivo',
    'nav.account': 'Account', 'nav.help': 'Aiuto e formazione', 'nav.compliancekit': 'Kit di conformità', 'nav.feeworksheet': 'Foglio costi',
    'nav.mcr': 'Report chiamate ipotecarie', 'nav.income': 'Calcolatore del reddito', 'nav.documents': 'Documenti',
    'nav.commissions': 'Commissioni', 'nav.automations': 'Automazioni', 'nav.marketingstudio': 'Studio Marketing', 'nav.emailanalytics': 'Analisi email',
    'nav.group.tools': 'Strumenti', 'nav.group.marketingops': 'Marketing e operazioni', 'nav.org': "Impostazioni dell'organizzazione",
    'nav.brand': 'Marchio e sito web', 'nav.billing': 'Fatturazione', 'nav.enterprise': 'Enterprise',
    'nav.workspace': 'Area di lavoro', 'add.loan': 'Nuova pratica', 'add.lead': 'Nuovo lead',
    'add.contact': 'Nuovo contatto', 'add.transaction': 'Nuova transazione', 'add.task': 'Nuova attività',
  },
  vi: {
    'nav.dashboard': 'Bảng điều khiển', 'nav.pipeline': 'Pipeline', 'nav.leads': 'Khách tiềm năng', 'nav.contacts': 'Liên hệ',
    'nav.loans': 'Hồ sơ vay', 'nav.calc': 'Máy tính', 'nav.ai': 'Audrey AI', 'nav.marketing': 'Tiếp thị',
    'nav.settings': 'Cài đặt', 'nav.refer': 'Giới thiệu', 'nav.more': 'Thêm',
    'action.new': 'Mới', 'action.signout': 'Đăng xuất', 'welcome': 'Chào mừng trở lại', 'lang.picker': 'Ngôn ngữ',
    'nav.calendar': 'Lịch', 'nav.deals': 'Giao dịch', 'nav.realestate': 'Bất động sản',
    'nav.reports': 'Báo cáo', 'nav.tasks': 'Công việc', 'nav.inbox': 'Hộp thư',
    'nav.account': 'Tài khoản', 'nav.help': 'Trợ giúp & đào tạo', 'nav.compliancekit': 'Bộ tuân thủ', 'nav.feeworksheet': 'Bảng phí',
    'nav.mcr': 'Báo cáo cuộc gọi thế chấp', 'nav.income': 'Máy tính thu nhập', 'nav.documents': 'Tài liệu',
    'nav.commissions': 'Hoa hồng', 'nav.automations': 'Tự động hóa', 'nav.marketingstudio': 'Studio tiếp thị', 'nav.emailanalytics': 'Phân tích email',
    'nav.group.tools': 'Công cụ', 'nav.group.marketingops': 'Tiếp thị & vận hành', 'nav.org': 'Cài đặt tổ chức',
    'nav.brand': 'Thương hiệu & trang web', 'nav.billing': 'Thanh toán', 'nav.enterprise': 'Doanh nghiệp',
    'nav.workspace': 'Không gian làm việc', 'add.loan': 'Hồ sơ vay mới', 'add.lead': 'Khách tiềm năng mới',
    'add.contact': 'Liên hệ mới', 'add.transaction': 'Giao dịch mới', 'add.task': 'Công việc mới',
  },
};

// Without a provider (tests, a stray render) the portal still reads in English.
const LangContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => STRINGS.en[k] || k });

export function LanguageProvider({ children, initial }) {
  const [lang, setLangState] = useState(() => {
    if (initial && STRINGS[initial]) return initial;
    try {
      const saved = localStorage.getItem('bfy_lang');
      if (saved && STRINGS[saved]) return saved;
      const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
      return STRINGS[nav] ? nav : 'en';
    } catch { return 'en'; }
  });

  // Persist choice locally + push to server so it survives across devices
  const setLang = (next) => {
    if (!STRINGS[next]) return;
    setLangState(next);
    try { localStorage.setItem('bfy_lang', next); } catch {}
    // Logged-out language flags (login/signup) stay local — no 401 per click (ticket 98 N16).
    if (getToken()) api.post('/users/me/language', { language: next }).catch(() => {});
  };

  // Reflect on <html> for CSS + a11y hooks
  useEffect(() => { document.documentElement.setAttribute('lang', lang); }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t: (key) => STRINGS[lang]?.[key] || STRINGS.en[key] || key,
  }), [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
